import { IModuleSchema, ModuleService } from '@/api/module';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { Button, Dialog, Modal, ModalRef } from 'alurkerja-ui';
import React, { useRef } from 'react';
import { useModuleDispatch } from '@/states/moduleHook';
import { archiveModule, deleteModule } from '@/states/moduleSlice';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { FaTrash } from 'react-icons/fa';

type DeleteModuleProps = {
  id: string;
  isArchive: boolean;
  onSuccess: () => void;
  onCloseCard?: () => void;
  module?: IModuleSchema;
};

export const DeleteModule: React.FC<DeleteModuleProps> = ({
  id,
  isArchive,
  onSuccess,
  onCloseCard,
  module,
}) => {
  const ref = useRef<ModalRef>(null);
  const dispatch = useModuleDispatch();
  const { mutate: deleteMutate, isPending: isDeleteLoading } =
    ModuleService.useDelete();
  const { mutate: archiveMutate, isPending: isArchiveLoading } =
    ModuleService.useArchive();
  const { mutate: unArchiveMutate, isPending: isUnarchiveLoading } =
    ModuleService.useUnarchive();

  const deleteAction = (id: string) => {
    dispatch(deleteModule({ moduleId: id }));
    deleteMutate(id, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Success to delete',
          callback: () => {
            onSuccess();
            onCloseCard?.();
            ref.current?.closeModal();
          },
        });
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to delete',
        });
      },
    });
  };

  const archiveAction = (id: string) => {
    dispatch(archiveModule({ moduleId: id, isArchive: true }));
    archiveMutate(id, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Success to archived',
          callback: () => {
            onSuccess();
            onCloseCard?.();
            ref.current?.closeModal();
          },
        });
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to archive',
        });
      },
    });
  };

  const unarchiveAction = (id: string) => {
    dispatch(archiveModule({ moduleId: id, isArchive: false }));
    unArchiveMutate(id, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Success to unarchived',
          callback: () => {
            onSuccess();
            ref.current?.closeModal();
          },
        });
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to unarchive',
        });
      },
    });
  };

  return (
    <Modal
      title={`Delete ${module?.submodule?.length ? 'Epic' : 'User Story'}`}
      ref={ref}
      triggerButton={
        <LinkButton icon={<FaTrash className="text-red-500" />} />
      }
    >
      {({ closeModal }) => {
        return (
          <div className="flex flex-col gap-4 p-4">
            <p className="text-gray-500">
              Are you sure you want to delete this? This action cannot be undone.
            </p>

            <div className="flex flex-row gap-2">
              <Button
                type="button"
                onClick={() => (isArchive ? unarchiveAction(id) : archiveAction(id))}
                loading={isArchive ? isUnarchiveLoading : isArchiveLoading}
              >
                {isArchive ? 'Unarchive' : 'Archive'}
              </Button>

              <Button
                type="button"
                color="red"
                onClick={() => deleteAction(id)}
                loading={isDeleteLoading}
              >
                Delete
              </Button>

              <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
            </div>
          </div>
        );
      }}
    </Modal>
  );
};
